// ============================================
// Journal Overlay
// ============================================

import { storyManager } from '../engine/story.js';
import { statisticsTracker } from '../systems/statistics.js';
import { L } from '../systems/localization.js';
import { getStorylineData } from '../systems/storylines.js';
import { renderProgressCard, renderStorylineGroup } from './components/progress-card.js';
import { isPat } from '../engine/pgate.js';

/** L10n helper: L.get(key) or fallback if key is missing. */
function _t(key, fb) {
    const v = L.get(key);
    return v !== key ? v : fb;
}

export function renderJournalTab(tabName, container) {
    container.innerHTML = '';
    const story = storyManager.currentStory;

    switch (tabName) {
        case 'hints': {
            const hints = [
                _t('journal.hint.1', 'Try visiting different locations to discover events.'),
                _t('journal.hint.2', 'Increase Femininity to unlock new clothes and encounters.'),
                _t('journal.hint.3', 'Check your phone for messages from NPCs.'),
                _t('journal.hint.4', 'The Salon offers hair, nails, and makeup services.'),
                _t('journal.hint.5', 'Your apartment has a computer for browsing and streaming.'),
            ];
            for (const text of hints) {
                const el = document.createElement('div');
                el.className = 'hint-item';
                el.textContent = text;
                container.appendChild(el);
            }
            break;
        }
        case 'progress': {
            const sv = (v) => { try { return story ? (story.variablesState[v] || 0) : 0; } catch(e) { return 0; } };
            const sb = (v) => { try { return story ? !!story.variablesState[v] : false; } catch(e) { return false; } };
            // Canonical storyline data (single source of truth shared with Phone
            // Progress App). Adapter wraps the modular shape into the journal's
            // multiPath/compute/detail shape used by the existing renderer.
            const _wrapDetail = (raw) => `<div style="margin-top:6px;font-size:11px;color:var(--text-muted);line-height:1.5">${L.interpolate(raw)}</div>`;
            const _canon = getStorylineData(story);
            const _gf = _canon.girlfriend;
            const _gfAdapter = {
                base: {
                    compute: () => _gf.val,
                    max: _gf.max,
                    detail: () => _wrapDetail(_gf.hint),
                },
                decisionUnlocked: _gf.decisionUnlocked,
                paths: _gf.paths.map(p => ({
                    key: p.key,
                    name: _t('journal.gf_path.' + p.key + '.name', p.name),
                    desc: _t('journal.gf_path.' + p.key + '.desc', p.desc),
                    activeWhen: p.activeWhen,
                    compute: p.val,
                    max: p.max,
                    detail: () => _wrapDetail(p.hint),
                })),
            };
            const storylines = [
                { name: _t('journal.progress.master.name', 'Master'), desc: _t('journal.progress.master.desc', 'Dominant encounters'), var: 'MasterRelationship', max: 10, category: 'main',
                  disabled: () => (story?.variablesState['master_intensity'] || 0) === 0,
                  paths: _canon.master.paths, decisionUnlocked: _canon.master.decisionUnlocked },
                { name: _t('journal.progress.master_orders.name', 'Master Orders'), desc: _t('journal.progress.master_orders.desc', 'Cage, Plug, Laser, Hair, Makeup'), vars: ['master_order_chastity','master_order_plug','master_order_laser','master_order_hair','master_order_makeup'], thresholds: [3,3,2,2,2], max: 5, category: 'main',
                  disabled: () => (story?.variablesState['master_intensity'] || 0) < 2,
                    // Per-order breakdown — shown when the card renders.
                    // Player report L3l0uch 2026-05-03 v0.2.5.5: stuck at MR cap
                    // with no clue which order to advance. Cage/Plug use the
                    // 4-stage progression (1=ordered, 2=bought, 3=worn).
                    // Laser/Hair/Makeup use the 3-stage one (1=ordered, 2=done)
                    // plus a sub-counter the dispatcher actually checks.
                    detail: () => {
                        const items = [
                            { label: _t('journal.master_orders.chastity', 'Cage'), v: sv('master_order_chastity'), cap: 3,
                              hint: !sb('owns_chastity') ? _t('journal.master_orders.cage_buy', 'visit Adult Store') :
                                    !sb('wearing_chastity') ? _t('journal.master_orders.cage_wear', 'equip the cage') : '' },
                            { label: _t('journal.master_orders.plug', 'Plug'), v: sv('master_order_plug'), cap: 3,
                              hint: !sb('owns_plug') ? _t('journal.master_orders.plug_buy', 'visit Adult Store') :
                                    !sb('wearing_plug') ? _t('journal.master_orders.plug_wear', 'equip the plug') : '' },
                            { label: _t('journal.master_orders.laser', 'Laser'), v: sv('master_order_laser'), cap: 2,
                              progress: { cur: sv('laser_sessions'), max: 5, label: _t('journal.master_orders.sessions', 'sessions') },
                              hint: sv('master_order_laser') === 1 ? _t('journal.master_orders.salon', 'visit Beauty Salon') : '' },
                            { label: _t('journal.master_orders.hair', 'Hair'), v: sv('master_order_hair'), cap: 2,
                              hint: sv('master_order_hair') === 1 && !sb('has_long_hair') && !sb('wearing_wig') ? _t('journal.master_orders.salon_hair', 'extensions at Beauty Salon or a wig from the Mall') : '' },
                            { label: _t('journal.master_orders.makeup', 'Makeup'), v: sv('master_order_makeup'), cap: 2,
                              progress: { cur: sv('makeup_skill'), max: 3, label: _t('journal.master_orders.skill', 'skill') },
                              hint: sv('master_order_makeup') === 1 ? _t('journal.master_orders.salon_makeup', 'practice at Beauty Salon') : '' },
                        ];
                        const lines = items.map(it => {
                            const done = it.v >= it.cap;
                            const icon = done ? '✅' : (it.v === 0 ? '○' : '⏳');
                            let sub = '';
                            if (done) {
                                sub = _t('journal.master_orders.done', 'done');
                            } else if (it.progress) {
                                sub = `${it.progress.cur}/${it.progress.max} ${it.progress.label}`;
                            } else if (it.v === 0) {
                                sub = _t('journal.master_orders.not_started', 'not started');
                            } else {
                                sub = _t('journal.master_orders.in_progress', 'in progress');
                            }
                            const hint = it.hint && !done ? ` <span style="color:var(--text-faint);font-size:10px">— ${it.hint}</span>` : '';
                            return `<div style="font-size:11px;color:var(--text-muted);line-height:1.5">${icon} <strong>${it.label}</strong>: ${sub}${hint}</div>`;
                        });
                        return `<div style="margin-top:6px">${lines.join('')}</div>`;
                    }
                },
                { name: _t('journal.progress.chastity.name', 'Chastity'), desc: _t('journal.progress.chastity.desc', 'Locked and learning'), var: 'chastity_days_worn', max: 21, category: 'main',
                  // 0.2.8: same 3-path tracker (Removable/Permanent/Walk Away)
                  // the phone Progress app consumes — pre-fix this row kept
                  // showing "Locked and learning" x/21 after Walk-Away or the
                  // permanent ceremony while the phone showed the path card.
                  paths: _canon.chastity.paths, decisionUnlocked: _canon.chastity.decisionUnlocked },
                { name: _t('journal.progress.girlfriend.name', 'Girlfriend'),
                  desc: _t('journal.progress.girlfriend.desc', 'Tension, discovery and arc'),
                  category: 'main',
                  disabled: () => !!story?.variablesState['girlfriend_disabled'],
                  multiPath: _gfAdapter },
                { name: _t('journal.progress.streaming.name', 'Streaming'), desc: _t('journal.progress.streaming.desc', 'Online fame'), compute: () => sb('stream_milestone_5') ? 5 : sb('stream_milestone_4') ? 4 : sb('stream_milestone_3') ? 3 : sb('stream_milestone_2') ? 2 : sb('stream_milestone_1') ? 1 : sv('streams_completed') > 0 ? 1 : 0, max: 5, category: 'work' },
                { name: _t('journal.progress.bar.name', 'Bar Career'), desc: _t('journal.progress.bar.desc', 'Bartending progression'), var: 'bar_outfit_tier', max: 3, category: 'work' },
                { name: _t('journal.progress.office.name', 'Office'), desc: _t('journal.progress.office.desc', 'Corporate submission'), var: 'office_boss_stage', max: 3, category: 'work',
                  paths: _canon.office.paths, decisionUnlocked: _canon.office.decisionUnlocked },
                { name: _t('journal.progress.maid.name', 'Maid Service'), desc: _t('journal.progress.maid.desc', 'Cleaning clients'), var: 'maid_clients_served', max: 10, category: 'work' },
                { name: _t('journal.progress.velvet.name', 'Velvet Lounge'), desc: _t('journal.progress.velvet.desc', "Vivienne's establishment"), var: 'velvet_stage', max: 3, category: 'work',
                  paths: _canon.velvet.paths, decisionUnlocked: _canon.velvet.decisionUnlocked },
                { name: _t('journal.progress.cafe.name', 'Maid Cafe'), desc: _t('journal.progress.cafe.desc', 'Nyan Nyan service'), var: 'cafe_stage', max: 3, category: 'work',
                  paths: _canon.cafe.paths, decisionUnlocked: _canon.cafe.decisionUnlocked },
                { name: _t('journal.progress.gym.name', 'Gym (Malik)'), desc: _t('journal.progress.gym.desc', 'Personal training'), var: 'trainer_stage', max: 4, category: 'npcs' },
                // The Gilded Cage cabaret (0.3, Patreon venue). Hidden until the
                // player has walked in once; cabaret_stage 0-3 (floor act / roster).
                { name: _t('journal.progress.cabaret.name', 'The Gilded Cage'), desc: _t('journal.progress.cabaret.desc', 'Stage career'), var: 'cabaret_stage', max: 3, category: 'work',
                  disabled: () => !sb('cabaret_discovered') },
                // BNWO University arc (0.3.1 descent rework): canonical entry from
                // systems/storylines.js — dynamic desc/hint track discovery, the
                // doubt walk-away pause, per-phase session count and the rest-day
                // cooloff. Hidden while the opt-in toggle/Patreon is off
                // (CONTENT_TOGGLES.md); val 0 keeps it out of the list until the
                // descent has actually begun.
                { name: _canon.bnwo.name, desc: _canon.bnwo.desc, category: 'npcs',
                  compute: () => _canon.bnwo.val, max: _canon.bnwo.max,
                  disabled: () => _canon.bnwo.status === 'hidden',
                  detail: () => _wrapDetail(_canon.bnwo.hint) },
                // Sissy Body Training (0.2.9, free since 0.3). Reads booty_tier/waist_tier
                // via sv() (story.variablesState direct — non-observed-var rule,
                // save-state-drift §7). Hidden entirely while the 0.2.9 kill-switch
                // is off OR the arc isn't unlocked; surfaces at the first tier-up.
                { name: _t('journal.progress.body_training.name', 'Body Training (Tyler)'),
                  desc: _t('journal.progress.body_training.desc', 'Booty and waist sculpting'),
                  category: 'npcs',
                  disabled: () => !sb('tyler_coach_unlocked'),
                  compute: () => sv('booty_tier') + sv('waist_tier'),
                  max: 6,
                  detail: () => {
                      const tierWord = (t) => t >= 3 ? _t('journal.body_training.dramatic', 'dramatic')
                          : t === 2 ? _t('journal.body_training.noticeable', 'noticeable')
                          : t === 1 ? _t('journal.body_training.subtle', 'subtle')
                          : _t('journal.body_training.none', 'no change yet');
                      const rows = [
                          `🍑 <strong>${_t('journal.body_training.booty', 'Booty')}</strong>: ${tierWord(sv('booty_tier'))} (${sv('booty_tier')}/3)`,
                          `📏 <strong>${_t('journal.body_training.waist', 'Waist')}</strong>: ${tierWord(sv('waist_tier'))} (${sv('waist_tier')}/3)`,
                      ];
                      return `<div style="margin-top:6px">${rows.map(r => `<div style="font-size:11px;color:var(--text-muted);line-height:1.5">${r}</div>`).join('')}</div>`;
                  } },
                // HypnoMind (0.2.9, FREED for 0.3, owner 2026-07-07). Sasha's phone-app conditioning arc.
                // Reads hypnomind_depth (0-100) + hypnomind_stage (0-3) via sv()
                // (non-observed-var rule, save-state-drift §7). Hidden while the
                // 0.2.9 kill-switch is off OR the app isn't unlocked.
                { name: _t('journal.progress.hypnomind.name', 'HypnoMind (Sasha)'),
                  desc: _t('journal.progress.hypnomind.desc', 'Conditioning depth'),
                  category: 'npcs',
                  disabled: () => !sb('hypnomind_unlocked'),
                  compute: () => Math.min(100, sv('hypnomind_depth')),
                  max: 100,
                  detail: () => {
                      const stageWord = sv('hypnomind_stage') >= 3 ? _t('journal.hypnomind.surrendered', 'Surrendered')
                          : sv('hypnomind_stage') === 2 ? _t('journal.hypnomind.deepening', 'Deepening')
                          : sv('hypnomind_stage') === 1 ? _t('journal.hypnomind.conditioning', 'Conditioning')
                          : _t('journal.hypnomind.not_started', 'Not started');
                      const rows = [
                          `🌀 <strong>${_t('journal.hypnomind.stage', 'Stage')}</strong>: ${stageWord} (${sv('hypnomind_stage')}/3)`,
                          `📊 <strong>${_t('journal.hypnomind.depth', 'Conditioning')}</strong>: ${Math.min(100, sv('hypnomind_depth'))}/100`,
                      ];
                      return `<div style="margin-top:6px">${rows.map(r => `<div style="font-size:11px;color:var(--text-muted);line-height:1.5">${r}</div>`).join('')}</div>`;
                  } },
                { name: _t('journal.progress.beach.name', 'Beach Bully'), desc: _t('journal.progress.beach.desc', 'Intimidation and blackmail arc'), var: 'beach_bully_stage', max: 5, category: 'npcs' },
                { name: _t('journal.progress.sugar.name', 'Sugar Daddy'), desc: _t('journal.progress.sugar.desc', 'Luxury and submission'), var: 'sugar_arrangement', max: 3, category: 'npcs' },
                { name: _t('journal.progress.spa.name', 'Spa (Dominic)'), desc: _t('journal.progress.spa.desc', 'Masseur encounters'), var: 'masseur_stage', max: 4, category: 'npcs' },
                { name: _t('journal.progress.clinic.name', 'Clinic (Dr. Mercer)'), desc: _t('journal.progress.clinic.desc', 'Medical appointments'), var: 'doctor_stage', max: 5, category: 'npcs' },
                { name: _t('journal.progress.hypno.name', 'Hypno'), desc: _t('journal.progress.hypno.desc', 'Trance sessions'), var: 'hypno_session_tier', max: 4, category: 'npcs' },
                { name: _t('journal.progress.tattoo.name', 'Tattoo (Jade)'), desc: _t('journal.progress.tattoo.desc', 'Ink and conversation'), var: 'jade_rapport', max: 5, category: 'npcs' },
                // v0.2.6 additions
                { name: _t('journal.progress.independent.name', 'Independent Path'),
                  desc: _t('journal.progress.independent.desc', 'Solo milestones (Day 5, 12, 18)'),
                  compute: () => (sb('independent_arc_1_seen') ? 1 : 0) + (sb('independent_arc_2_seen') ? 1 : 0) + (sb('independent_arc_3_seen') ? 1 : 0),
                  max: 3, category: 'main' },
                { name: _t('journal.progress.doxxing.name', 'Doxxing Arc'),
                  desc: _t('journal.progress.doxxing.desc', 'Streaming consequence chain'),
                  var: 'doxxing_arc_stage', max: 4, category: 'main' },
                // Kennel / Mira — canonical entry from storylines.js since 0.3.1
                // (replaces the legacy 11-session bar; session detail lives in
                // the canonical desc). Expansion milestones armed+Patreon only;
                // row drops entirely when the Pet Play pref is off (§10.2).
                { name: _canon.kennel.name, desc: _canon.kennel.desc, category: 'npcs',
                  compute: () => _canon.kennel.val, max: _canon.kennel.max,
                  disabled: () => _canon.kennel.status === 'hidden',
                  detail: () => _wrapDetail(_canon.kennel.hint) },
                { name: _t('journal.progress.resort.name', 'Resort (Dante)'),
                  desc: _t('journal.progress.resort.desc', 'Cabana arc with Dante'),
                  var: 'dante_stage', max: 5, category: 'npcs' },
                { name: _t('journal.progress.iris.name', 'Resort (Iris)'),
                  desc: _t('journal.progress.iris.desc', 'Front desk encounters'),
                  var: 'iris_stage', max: 3, category: 'npcs' },
                { name: _t('journal.progress.dating.name', 'GrindrR Dating'),
                  desc: _t('journal.progress.dating.desc', 'Match milestones across NPCs'),
                  compute: () => {
                      const tracks = ['tyler', 'kai', 'owen', 'sasha', 'eloise', 'raven', 'scar'];
                      let total = 0;
                      for (const t of tracks) {
                          const matched = sb('matched_' + t);
                          if (matched) total += Math.min(3, sv('dating_' + t));
                      }
                      return Math.min(21, total);
                  },
                  max: 21, category: 'npcs' },

                // v0.2.7 storyline entries
                { name: _t('journal.progress.basement_workshop.name', 'Basement Workshop'),
                  desc: _t('journal.progress.basement_workshop.desc', 'Master + Vincent invite arcs'),
                  compute: () => {
                      if (!sb('apartment_has_basement')) return 0;
                      // Vincent's invite arc is forever-Patreon: dating_vincent >= 2 is gated by
                      // is_patreon() in DatingVincent.ink, so workshop_vincent_stage is unreachable
                      // on the free build. Don't count it (and cap max at 4 below) so a free player
                      // sees 4/4 instead of a permanently-stuck 4/7.
                      const vincent = isPat() ? Math.min(3, sv('workshop_vincent_stage')) : 0;
                      return 1 + Math.min(3, sv('workshop_master_stage')) + vincent;
                  },
                  max: isPat() ? 7 : 4, category: 'main' },
                { name: _t('journal.progress.playroom.name', 'Playroom'),
                  desc: _t('journal.progress.playroom.desc', 'Girlfriend / Kai / Hana invite arcs'),
                  compute: () => {
                      if (!sb('apartment_has_playroom')) {
                          // Surface nest-tier progression for the unlock
                          return Math.min(2, sv('apartment_nest_tier'));
                      }
                      return 3 + Math.min(3, sv('playroom_gf_stage')) + Math.min(3, sv('playroom_kai_stage')) + Math.min(3, sv('playroom_hana_stage'));
                  },
                  max: 12, category: 'main' },
                { name: _t('journal.progress.comfort_training.name', 'Comfort Training'),
                  desc: _t('journal.progress.comfort_training.desc', '4 training lines x 8 sessions for permanent +10 Energy'),
                  compute: () => {
                      return Math.min(8, sv('waist_trainer_count') + sv('waist_trainer_adv_count') + sv('waist_trainer_exp_count')) +
                             Math.min(8, sv('nipple_clamps_count')) +
                             Math.min(8, sv('cuffs_count')) +
                             Math.min(8, sv('heels_count'));
                  },
                  max: 32, category: 'work' },
                { name: _t('journal.progress.sarah_dom.name', 'Sarah Domme'),
                  desc: _t('journal.progress.sarah_dom.desc', 'Branches off Acceptance'),
                  compute: () => {
                      const arc = sv('sarah_arc');
                      // sarah_dom_stage never existed as an Ink VAR — this froze the
                      // Domme bar at 1/4 forever (route "looks stuck" even as it advances).
                      // Real depth var is sarah_dom_intensity (1=bedroom, 2=rules, 3=cuck).
                      // foundby VPS+Discord 0.2.9.1.1. See feedback_comfort_training_display_dead_vars.
                      if (arc === 5) return Math.min(4, 1 + sv('sarah_dom_intensity'));
                      // Pre-route: surface the signal counter so the player sees it building
                      // toward the >=4 unlock threshold (bar max is 4).
                      return Math.min(4, sv('sarah_dom_signal'));
                  },
                  max: 4, category: 'main',
                  disabled: () => sb('girlfriend_disabled') || sb('girlfriend_broken_up') },
                { name: _t('journal.progress.streaming_regulars.name', 'Streaming Regulars'),
                  desc: _t('journal.progress.streaming_regulars.desc', 'Loyalty across 5 named viewers'),
                  compute: () => {
                      const r = ['xxfemboi', 'silent_admirer', 'sub_in_training', 'the_doctor', 'bigspender'];
                      let total = 0;
                      for (const n of r) total += Math.min(5, sv('regular_' + n + '_loyalty'));
                      return Math.min(25, total);
                  },
                  max: 25, category: 'work' },
                // SMS side-chains (one-shot, can be missed via 'Ignore' choice)
                { name: _t('journal.progress.fan.name', 'Hot Fan'),
                  desc: _t('journal.progress.fan.desc', 'Streamer subscriber SMS chain'),
                  var: 'sms_fan_stage', max: 3, category: 'npcs' },
                { name: _t('journal.progress.cuckold.name', 'Cuckold SMS'),
                  desc: _t('journal.progress.cuckold.desc', 'Anonymous SMS chain'),
                  var: 'sms_cuckold_stage', max: 3, category: 'main' },
                { name: _t('journal.progress.custom_file.name', 'Custom Hypno File'),
                  desc: _t('journal.progress.custom_file.desc', "Sasha's personal track"),
                  var: 'custom_file_stage', max: 3, category: 'npcs' },
                { name: _t('journal.progress.gloryhole_regular.name', 'Glory Hole Regular'),
                  desc: _t('journal.progress.gloryhole_regular.desc', 'Face-recognition arc'),
                  // 0.2.9: stages 4-5 take the regular off-site (dinner →
                  // townhouse → collar). FREED + v029 kill-switch stripped 2026-07-07,
                  // so the full 5-stage bar shows for everyone.
                  var: 'gloryhole_regular_stage', max: 5, category: 'npcs' },
                // Major Beats — counts 10 intro_* one-shot watershed flags.
                // Pre-fix the flags were declared, set at their trigger points,
                // but never read anywhere. Now surfaced as a 0-10 progress card
                // so the player can see their "first-time" milestones at a glance.
                // (Audit 2026-05-23 — wired per user instruction "build it,
                // don't delete it".)
                { name: _t('journal.progress.intros.name', 'Major Beats'),
                  desc: _t('journal.progress.intros.desc', 'First-time watersheds'),
                  vars: ['intro_mirror_fem', 'intro_bar_dress', 'intro_chastity_first',
                         'intro_fem_50', 'intro_first_date_man', 'intro_sarah_aftermath',
                         'intro_chastity_orgasm', 'intro_master_tender', 'intro_stream_1000',
                         'intro_endgame_eve'],
                  thresholds: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
                  max: 10, category: 'main',
                  detail: () => {
                      const beats = [
                          { v: 'intro_mirror_fem', label: _t('journal.intros.mirror_fem', 'Mirror, first soft look') },
                          { v: 'intro_bar_dress', label: _t('journal.intros.bar_dress', 'First dress shift') },
                          { v: 'intro_chastity_first', label: _t('journal.intros.chastity_first', 'First cage') },
                          { v: 'intro_fem_50', label: _t('journal.intros.fem_50', 'Halfway feminine') },
                          { v: 'intro_first_date_man', label: _t('journal.intros.first_date_man', 'First date with a man') },
                          { v: 'intro_sarah_aftermath', label: _t('journal.intros.sarah_aftermath', 'After Sarah found out') },
                          { v: 'intro_chastity_orgasm', label: _t('journal.intros.chastity_orgasm', 'Caged climax') },
                          { v: 'intro_master_tender', label: _t('journal.intros.master_tender', 'Master, tender once') },
                          { v: 'intro_stream_1000', label: _t('journal.intros.stream_1000', '1000 stream followers') },
                          { v: 'intro_endgame_eve', label: _t('journal.intros.endgame_eve', 'The eve of the ending') },
                      ];
                      const lines = beats.map(b => {
                          const done = sb(b.v);
                          return (done ? '✅ ' : '○ ') + b.label;
                      }).join('\n');
                      return _wrapDetail(lines);
                  } },
            ];
            // Shortcut row: launches the richer Phone Progress App (same data,
            // 3-tab UI with People + Storylines + Achievements).
            const _shortcutRow = document.createElement('div');
            _shortcutRow.className = 'journal-progress-shortcut';
            _shortcutRow.innerHTML = `<button class="journal-progress-shortcut-btn" type="button">📊 ${_t('journal.progress.open_phone_app', 'Open Progress App for the full picture')}</button>`;
            _shortcutRow.querySelector('button').addEventListener('click', () => {
                // Close journal first so the phone overlay isn't shown behind it.
                const overlay = document.getElementById('overlay-journal');
                if (overlay) overlay.classList.add('hidden');
                try {
                    const phone = window._phone;
                    if (phone) {
                        if (!phone._isOpen) phone.open();
                        if (phone._showProgressView) phone._showProgressView(1); // 1 = Storylines tab
                    }
                } catch (e) { console.warn('[journal] open progress app failed:', e); }
            });
            container.appendChild(_shortcutRow);
            // Group by category
            const categories = {
                main: _t('journal.cat.main', 'Main Story'),
                work: _t('journal.cat.work', 'Work and Career'),
                npcs: _t('journal.cat.npcs', 'NPC Storylines'),
            };
            let currentCat = '';
            // Sort by category order
            const catOrder = ['main', 'work', 'npcs'];
            storylines.sort((a, b) => catOrder.indexOf(a.category) - catOrder.indexOf(b.category));
            // Convert a journal-shape storyline into the canonical shape that
            // renderStorylineGroup expects. Skip storylines that haven't started.
            const _toCanonical = (sl) => {
                if (sl.multiPath) {
                    const mp = sl.multiPath;
                    const baseVal = mp.base.compute();
                    const hasActive = mp.paths.some(p => p.activeWhen());
                    if (baseVal <= 0 && !hasActive) return null;
                    return {
                        name: sl.name, desc: sl.desc,
                        val: baseVal, max: mp.base.max,
                        status: baseVal >= mp.base.max ? 'complete' : 'active',
                        hint: mp.base.detail ? mp.base.detail() : '',
                        paths: mp.paths.map(p => ({
                            key: p.key, name: p.name, desc: p.desc,
                            activeWhen: p.activeWhen,
                            val: p.compute, max: p.max,
                            hint: p.detail ? p.detail() : '',
                        })),
                        decisionUnlocked: mp.decisionUnlocked,
                    };
                }
                let baseVal;
                if (sl.compute) baseVal = sl.compute();
                else if (sl.vars) {
                    baseVal = 0;
                    for (let i = 0; i < sl.vars.length; i++) {
                        if ((story?.variablesState[sl.vars[i]] || 0) >= sl.thresholds[i]) baseVal++;
                    }
                } else baseVal = story ? (story.variablesState[sl.var] || 0) : 0;
                // Direct-path entries: storyline carries `paths` + `decisionUnlocked` from
                // systems/storylines.js. Still show the base card if any progress.
                const hasActivePath = sl.paths && sl.paths.some(p => p.activeWhen());
                if (baseVal <= 0 && !hasActivePath) return null;
                const done = baseVal >= sl.max;
                return {
                    name: sl.name, desc: sl.desc,
                    val: baseVal, max: sl.max,
                    status: done ? 'complete' : 'active',
                    hint: (sl.detail && !done) ? sl.detail() : '',
                    paths: sl.paths,
                    decisionUnlocked: sl.decisionUnlocked,
                };
            };

            for (const sl of storylines) {
                if (sl.disabled && sl.disabled()) continue;
                const canonical = _toCanonical(sl);
                if (!canonical) continue;
                // Category header (only emit when a renderable card is about to land)
                if (sl.category !== currentCat) {
                    currentCat = sl.category;
                    const header = document.createElement('div');
                    header.className = 'journal-category-header';
                    header.textContent = categories[currentCat] || currentCat;
                    container.appendChild(header);
                }
                renderStorylineGroup(container, canonical, { showSpoilers: true });
            }
            break;
        }
        case 'calendar': {
            const day = story ? (story.variablesState['Day'] || 1) : 1;
            const timeSlot = story ? (story.variablesState['TimeSlot'] || 0) : 0;
            const _tKeys = ['time.morning', 'time.lunch', 'time.evening', 'time.night'];
            const _tFB = ['Morning', 'Lunch', 'Evening', 'Night'];
            const _tk = _tKeys[timeSlot] || _tKeys[0];
            const _tv = L.get(_tk);
            const _tsName = _tv !== _tk ? _tv : (_tFB[timeSlot] || 'Morning');
            const _dk = L.get('desktop.day');
            const _dayStr = _dk !== 'desktop.day' ? _dk.replace('{0}', day) : `Day ${day}`;
            const _wdKeys = ['day.mon', 'day.tue', 'day.wed', 'day.thu', 'day.fri', 'day.sat', 'day.sun'];
            const _wdFB = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
            const _wdKey = _wdKeys[(day - 1) % 7];
            const _weekday = L.get(_wdKey) !== _wdKey ? L.get(_wdKey) : _wdFB[(day - 1) % 7];
            const el = document.createElement('div');
            el.style.cssText = 'padding: 16px; text-align: center;';
            el.innerHTML = `
                <div style="font-size: 32px; font-weight: 700; color: var(--rose); margin-bottom: 8px;">${_dayStr}</div>
                <div style="font-size: 16px; color: var(--text-soft); margin-bottom: 4px;">${_weekday}</div>
                <div style="font-size: 14px; color: var(--text-dim);">${_tsName}</div>
                <div style="margin-top: 16px; font-size: 11px; color: var(--text-faint);">${_t('journal.calendar.rent_note', 'Rent is due every 7 days.')}</div>
            `;
            container.appendChild(el);
            break;
        }
        case 'statistics': {
            const statKeys = [
                { key: 'blowjob', label: _t('journal.stats.blowjob', 'Blowjobs') },
                { key: 'anal', label: _t('journal.stats.anal', 'Anal') },
                { key: 'hookup', label: _t('journal.stats.hookup', 'Hookups') },
                { key: 'strip', label: _t('journal.stats.strip', 'Stripteases') },
                { key: 'edge', label: _t('journal.stats.edge', 'Edging') },
                { key: 'glory_hole', label: _t('journal.stats.glory_hole', 'Glory Holes') },
            ];
            for (const s of statKeys) {
                const val = statisticsTracker ? statisticsTracker.getStat(s.key) : 0;
                const row = document.createElement('div');
                row.className = 'journal-stat-row';
                row.innerHTML = `<span>${s.label}</span><span class="journal-stat-val">${val}</span>`;
                container.appendChild(row);
            }
            break;
        }
    }
}

export const journalUI = {
    init() {
        const tabs = document.querySelectorAll('.journal-tab');
        const content = document.getElementById('journal-content');
        if (!tabs.length || !content) return;

        tabs.forEach(tab => {
            tab.addEventListener('click', () => {
                tabs.forEach(t => t.classList.remove('active'));
                tab.classList.add('active');
                renderJournalTab(tab.dataset.tab, content);
            });
        });
    },
    /** Render the currently-active tab. Call after opening the overlay so the
     *  initial view (Hints) is populated instead of being empty. */
    renderActive() {
        const active = document.querySelector('.journal-tab.active');
        const content = document.getElementById('journal-content');
        if (active && content) renderJournalTab(active.dataset.tab, content);
    }
};
